import { useState, useMemo, useEffect, useCallback } from 'react';

import { getModalConfig } from '../LocationsContainer/getModalConfig';
import {
  filterUnassignedTurbines,
  transformTurbinesForModal
} from '../LocationsContainer/utils';
import styles from '../styles.module.css';
import TurbineIcon from '@/assets/icons/turbine.svg?react';
import {
  AdminCardBody,
  AdminCardHeader,
  AdminTableBody
} from '@/components/AdminToolComponents/card';
import DataTable from '@/components/DataTable';
import Loader from '@/components/Form/Loader';
import PlusButton from '@/components/Form/PlusButton';
import SearchComponent from '@/components/LocationDetails/searchComponent';
import { getTurbinesColumns } from '@/components/LocationDetails/turbinesColumns';
import useModal from '@/context/ModalContext/useModal';
import { useGetUnassignedTurbines } from '@/hooks/services/Locations';
import { TurbineApiData } from '@/types/turbines';

export type OnAssignTurbines = (
  turbines: TurbineApiData[],
  options?: { onSuccess?: () => void }
) => Promise<void>;

export type OnUnassignTurbine = (turbine: TurbineApiData) => Promise<void>;

interface TurbinesAssignedTableProps {
  turbinesForTable: TurbineApiData[];
  canAssignUnassignTurbines: boolean;
  onAssignTurbines: OnAssignTurbines;
  onUnassignTurbine: OnUnassignTurbine;
}

export const TurbinesAssignedTable = ({
  turbinesForTable,
  canAssignUnassignTurbines = false,
  onAssignTurbines,
  onUnassignTurbine
}: TurbinesAssignedTableProps) => {
  const { updateModalContent, closeModal } = useModal();
  const [isTurbineModalOpen, setIsTurbineModalOpen] = useState(false);
  const [turbinesSearchTerm, setTurbinesSearchTerm] = useState('');
  const [selectedTurbines, setSelectedTurbines] = useState<TurbineApiData[]>(
    []
  );

  const {
    data: dataUnassignedTurbines,
    isLoading: isLoadingUnassignedTurbines,
    error: errorUnassignedTurbines,
    isError: isErrorUnassignedTurbines
  } = useGetUnassignedTurbines({
    useQueryOptions: {
      enabled: isTurbineModalOpen,
      staleTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    }
  });

  const filteredTurbines = useMemo(() => {
    const transformedTurbines = transformTurbinesForModal(
      dataUnassignedTurbines?.data,
      isErrorUnassignedTurbines
    );
    return filterUnassignedTurbines(transformedTurbines, turbinesSearchTerm);
  }, [dataUnassignedTurbines, turbinesSearchTerm, isErrorUnassignedTurbines]);

  const handleCloseTurbineModal = useCallback(() => {
    closeModal();
    setIsTurbineModalOpen(false);
    setTurbinesSearchTerm('');
    setSelectedTurbines([]);
  }, [closeModal]);

  const handleTurbinesSearchChange = useCallback((value: string) => {
    setTurbinesSearchTerm(value);
  }, []);

  const handleOpenAssignModal = useCallback(() => {
    setTurbinesSearchTerm('');
    setSelectedTurbines([]);
    setIsTurbineModalOpen(true);
  }, []);

  const updateTurbineModalContent = useCallback(() => {
    if (!isTurbineModalOpen) return;

    const config = getModalConfig(
      'turbine',
      [],
      [],
      filteredTurbines || [],
      handleCloseTurbineModal,
      selectedTurbines,
      (turbines: TurbineApiData[]) =>
        onAssignTurbines(turbines, { onSuccess: handleCloseTurbineModal }),
      [],
      undefined,
      [],
      undefined
    );

    const createModalContent = () => {
      if (isErrorUnassignedTurbines) {
        return (
          <div className={styles.errorContainer}>
            <p>Error loading turbines:</p>
            <p>{errorUnassignedTurbines?.message || 'Unknown error'}</p>
          </div>
        );
      }

      if (isLoadingUnassignedTurbines) {
        return (
          <div className={styles.formModal}>
            <SearchComponent
              icon={config.icon || null}
              title={config.title || ''}
              onSearchChange={handleTurbinesSearchChange}
            />
            <div className={styles.formBody}>
              <div className={styles.loadingContainer}>
                <Loader size="large" />
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className={styles.formModal}>
          <SearchComponent
            icon={config.icon || null}
            title={config.title || ''}
            onSearchChange={handleTurbinesSearchChange}
          />
          <div className={styles.formBody}>
            <DataTable
              rows={(config.rows as TurbineApiData[]) || []}
              className={styles.dataTableModal}
              columns={config.columns || []}
              selectable={true}
              selectedRows={selectedTurbines}
              onRowSelectionChange={setSelectedTurbines}
              getRowId={row => row.id}
              initialState={{
                sorting: {
                  field: 'id',
                  sort: 'asc'
                }
              }}
            />
          </div>
        </div>
      );
    };

    updateModalContent(createModalContent(), {
      modalButtons: config.modalButtons
    });
  }, [
    isTurbineModalOpen,
    isErrorUnassignedTurbines,
    errorUnassignedTurbines,
    isLoadingUnassignedTurbines,
    filteredTurbines,
    selectedTurbines,
    handleCloseTurbineModal,
    handleTurbinesSearchChange,
    onAssignTurbines,
    updateModalContent
  ]);

  useEffect(() => {
    if (isTurbineModalOpen) {
      updateTurbineModalContent();
    }
  }, [isTurbineModalOpen, updateTurbineModalContent]);

  const handleUnassignClick = useCallback(
    (turbine: TurbineApiData) => {
      updateModalContent(null, {
        modalTitle: 'Unassign Turbine',
        modalIcon: TurbineIcon,
        className: styles.unassignModalContainer,
        modalButtons: [
          {
            label: 'CANCEL',
            onClick: () => {
              closeModal();
            },
            variant: 'cancelBlue'
          },
          {
            label: 'UNASSIGN TURBINE',
            onClick: async () => {
              try {
                await onUnassignTurbine(turbine);
              } catch (error) {
                console.error('Error in unassign turbine:', error);
              }
            },
            variant: 'okBlue',
            isLoader: true
          }
        ],
        alignButtons: 'center',
        contentTitle: 'You are about to unassign',
        contentSubtitle: `Turbine ${turbine.id}`,
        contentDescription: (
          <div className={styles.contentDescription}>
            <span>Are you sure you want to proceed?</span>
          </div>
        )
      });
    },
    [updateModalContent, closeModal, onUnassignTurbine]
  );

  return (
    <div className={styles.adminContainer}>
      <AdminCardHeader
        left={
          <>
            <TurbineIcon className={styles.adminIconHeader} />
            <h2 className={styles.adminTitle}>Turbines</h2>
          </>
        }
        right={
          canAssignUnassignTurbines ? (
            <PlusButton
              onClick={handleOpenAssignModal}
              label="Assign a turbine"
            />
          ) : undefined
        }
      />

      {turbinesForTable.length > 0 ? (
        <AdminTableBody>
          <DataTable
            rows={turbinesForTable}
            className={styles.dataTable}
            columns={getTurbinesColumns(
              canAssignUnassignTurbines,
              handleUnassignClick
            )}
            initialState={{
              sorting: {
                field: 'id',
                sort: 'asc'
              }
            }}
          />
        </AdminTableBody>
      ) : (
        <AdminCardBody>
          <div className={styles.adminContainerCenteredRow}>
            <h4 className={styles.adminText}>
              No turbines have been assigned yet
            </h4>
          </div>
        </AdminCardBody>
      )}
    </div>
  );
};
